import { forwardRef, Suspense, useEffect, useRef, useState } from 'react';
import type { CarouselAPI } from '@elfsight/lib-carousel';
import type { LayoutAPI } from '@elfsight/widget-ui';
import {
  isTransparentBackground,
  mapSliderLayoutSettings,
  SliderLayout,
  useDebounceCallback
} from '@elfsight/widget-ui';
import { useData } from '../../data';
import { usePopup } from '../../popup';
import { useSocialFeedSettings } from '../../utils';
import { useLayout } from '../LayoutProvider';
import { renderLoader, renderPost } from '../utils';

type AutoplayControlsType = {
  start: (() => void) | undefined;
  stop: (() => void) | undefined;
};

const DEBOUNCE_CALLBACK_DELAY = 350;

export const Slider = forwardRef<CarouselAPI & LayoutAPI, unknown>(
  (_props, forwardingRef) => {
    const settings = useSocialFeedSettings();
    const {
      renderThumbnailMode,
      sliderAutoplay,
      postTextColor,
      widgetBackground,
      widgetHorizontalPadding
    } = settings;
    const dataSource = useData();
    const { isOpen } = usePopup();
    const { setRerender, setCarouselVisibleIndexes } = useLayout();

    const sliderSettings = mapSliderLayoutSettings(settings);

    const autoplayIsRunningRef = useRef(false);
    const [autoplayControls, setAutoplayControls] =
      useState<AutoplayControlsType>({
        start: undefined,
        stop: undefined
      });

    useEffect(() => {
      if (forwardingRef && 'current' in forwardingRef) {
        setRerender(forwardingRef.current?.updateAutoHeight);
        setAutoplayControls({
          start: () => forwardingRef.current?.autoplayStart,
          stop: () => forwardingRef.current?.autoplayStop
        });
      }
    }, [setRerender, forwardingRef]);

    // "Костыль", чтоб избежать конфликта с pauseOnHover запуска/остановки карусели
    const debounceAutoplayStop = useDebounceCallback(() => {
      autoplayControls.stop?.();
    }, DEBOUNCE_CALLBACK_DELAY);
    const debounceAutoplayStart = useDebounceCallback(
      () => autoplayControls.start?.(),
      DEBOUNCE_CALLBACK_DELAY
    );

    useEffect(() => {
      const shouldStartAutoplay = sliderAutoplay && !isOpen;

      if (shouldStartAutoplay && !autoplayIsRunningRef.current) {
        debounceAutoplayStart();
        autoplayIsRunningRef.current = true;
      }

      if (!shouldStartAutoplay && autoplayIsRunningRef.current) {
        debounceAutoplayStop();
        autoplayIsRunningRef.current = false;
      }
    }, [
      isOpen,
      sliderAutoplay,
      debounceAutoplayStart,
      debounceAutoplayStop,
      renderThumbnailMode
    ]);

    return (
      <Suspense fallback={null}>
        <SliderLayout
          {...sliderSettings}
          ref={forwardingRef}
          dataSource={dataSource}
          // @ts-expect-error unknown не может применится для типа DataValue
          renderItem={renderPost}
          renderLoader={renderLoader(postTextColor)}
          reportSlideChangeInstantly={
            sliderSettings.swipeDesktopEnable ||
            sliderSettings.swipeMobileEnable
          }
          onVisibleItemsChange={setCarouselVisibleIndexes}
          containerHorizontalPadding={
            isTransparentBackground(widgetBackground)
              ? undefined
              : widgetHorizontalPadding
          }
        />
      </Suspense>
    );
  }
);
